/**
 * BXGY Promo — theme integration (Marini Q2 + future BXGYs)
 * ────────────────────────────────────────
 * Surfaces the Buy 6+ SCMS, get 1 free Hyla3D (1:1 scaling) promotion
 * inside the Marini vendor cart card on /carts. Auto-adds free Hyla3D
 * Serum on threshold cross, lets the user rebalance Serum vs Cream
 * inline, and reconciles ±1 on incremental SCMS quantity changes.
 *
 * Source of truth:
 *   - GET /api/storefront/active-promos on BR Connect returns the
 *     current bxgy_1to1_scaling promos with thresholds + product GIDs.
 *   - Cart state from /cart.js (Shopify standard).
 *   - On SCMS qty changes the multi-vendor cart manager dispatches
 *     `cart:updated` — we listen and re-render.
 *
 * Identification of products in cart:
 *   - Each cart line carries `product_id` (numeric). We compare against
 *     the numeric IDs extracted from the gid://shopify/Product/<id> in
 *     the promo config.
 *
 * Free-line tagging for safe later mutation:
 *   - When we add a Hyla3D line we set its `properties._marini_q2_free`
 *     to "1". On subsequent rebalances we only modify lines that carry
 *     that property — never touch a line the customer added themselves
 *     at full price.
 */
(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────────
     PROMO_CONFIG — single source of truth for everything that
     would otherwise be hardcoded across this file.

     Round 2 plan: every field tagged `FROM_CAMPAIGN_API` will be
     populated from BR Connect's /api/storefront/active-promos
     response (new fields proposed: brandFreeName, valueLabel,
     flyerUrl, ctaSuffix, defaultFreeSlot). Fields tagged
     `FROM_PRODUCT` will be resolved at runtime via Shopify's
     `/products/{id}.js` for each numeric ID in `freeProductIds`,
     yielding handles + slot labels without anyone hand-maintaining
     this map. Once that lands, the only literals here will be the
     hardcoded API path + campaignType slug.

     Until then this object lets us swap per-campaign behavior with
     one diff instead of 12.
  ────────────────────────────────────────────────────────────── */
  var PROMO_CONFIG = {
    /* ─── Source / API (env constants — never per-campaign) ── */
    brConnectBase: window.__beautyRepConnectBase || 'https://beautyrepconnect.com',
    activePromosPath: '/api/storefront/active-promos',
    campaignType: 'bxgy_1to1_scaling',

    /* ─── Identity (per-campaign — FROM_CAMPAIGN_API in R2) ── */
    sourceLabel: 'bxgy-promo',     // pubsub source filter + dataset prefix
    freeFlag: '_marini_q2_free',        // line-item property tagging "ours"

    /* ─── Copy bits (per-campaign — FROM_CAMPAIGN_API in R2) ── */
    brandFreeName: 'Hyla3D',            // appears in "+N Free Hyla3D" CTA suffix
    freeFlagShortLabel: 'Free Q2',      // appears in the "tagged Free Q2" full-price hint
    defaultFreeSlot: 'serum',           // which slot gets auto-added when threshold is crossed

    /* ─── Free product slots — fallback when API has no slots[] ──
       Round 2B preferred path: BR Connect surfaces slots[] in the
       campaign response (see docs/bxgy-api-contract.md), each entry
       carrying { productId, label, handle }. When that's missing we
       fall back to the maps below — Q2-only cheat-sheet. */
    freeProductHandleByNumericId: {
      '10097435869505': 'serum',        // hyla3d-serum
      '10097436295489': 'cream',        // hyla3d-cream
    },
    freeProductHandlesBySlot: {
      serum: 'hyla3d-serum',
      cream: 'hyla3d-cream',
    },

    /* ─── Value framing — fallback for API retailValue* fields ───
       Anthony's flyer frames freebies in RETAIL terms (A$200/each),
       not WSP. /products/{h}.js returns WSP, which would understate
       savings vs the brand's marketing copy. We surface the retail
       figure so the qualified-state total matches the per-freebie
       label. Once BR Connect ships retailValuePerFreebieCents +
       retailValueLabel on the campaign object, these defaults are
       overridden per-promo. */
    retailValuePerFreebieCents: 20000,  // A$200.00 — Marini Q2 default
    retailValueLabel: 'A$200',
    flyerUrl: 'https://cdn.shopify.com/s/files/1/0906/8717/7025/files/SCMS_Hyla3D_Q2_Promo_Practice_1.pdf?v=1777589197',
  };

  // Back-compat short aliases — keep all the existing call sites tiny.
  var BR_CONNECT_BASE = PROMO_CONFIG.brConnectBase;
  var ACTIVE_PROMOS_PATH = PROMO_CONFIG.activePromosPath;
  var FREE_FLAG = PROMO_CONFIG.freeFlag;
  var REQUEST_DEBOUNCE_MS = 300;

  /* ────────────────────────────────────────
     Tiny utilities
  ──────────────────────────────────────── */

  function gidToNumericId(gid) {
    if (!gid || typeof gid !== 'string') return null;
    var match = gid.match(/(\d+)$/);
    return match ? match[1] : null;
  }

  function fetchJson(url, init) {
    return fetch(url, init).then(function (res) {
      if (!res.ok) throw new Error(url + ' → ' + res.status);
      return res.json();
    });
  }

  // 429-aware fetchers. Storefront throttle is per-session and burst-y;
  // two retries with exponential backoff covers the typical recovery
  // window. JSON variant for GETs that should produce JSON; raw variant
  // for POSTs whose response we parse explicitly (so we can branch on
  // status codes before consuming the body).
  function fetchResilient(url, init, retriesLeft) {
    if (typeof retriesLeft !== 'number') retriesLeft = 2;
    var backoffs = [1500, 3000];
    var attempt = 2 - retriesLeft;
    return fetch(url, init).then(function (res) {
      if (res.status === 429 && retriesLeft > 0) {
        var wait = backoffs[Math.min(attempt, backoffs.length - 1)];
        return new Promise(function (resolve) { setTimeout(resolve, wait); })
          .then(function () { return fetchResilient(url, init, retriesLeft - 1); });
      }
      return res;
    }).catch(function (err) {
      // Network error (CORS, offline, etc.) — retry once.
      if (retriesLeft > 0) {
        var wait = backoffs[Math.min(attempt, backoffs.length - 1)];
        return new Promise(function (resolve) { setTimeout(resolve, wait); })
          .then(function () { return fetchResilient(url, init, retriesLeft - 1); });
      }
      throw err;
    });
  }

  function fetchJsonResilient(url, init) {
    return fetchResilient(url, init).then(function (res) {
      if (!res.ok) throw new Error(url + ' → ' + res.status);
      return res.json();
    });
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = arguments;
      var ctx = this;
      if (t) clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  // Read the current customer's source_brand metafield, injected by
  // theme.liquid as <meta name="br-customer-brand" content="…">. Empty
  // for anonymous visitors. Used to gate brand-scoped promos so we
  // never auto-inject freebies for a customer whose Shopify segment
  // doesn't match the campaign — those freebies wouldn't be discounted
  // at checkout (segment-gated) and the customer would pay full price.
  function getCustomerBrand() {
    var meta = document.querySelector('meta[name="br-customer-brand"]');
    return meta ? (meta.getAttribute('content') || '').toLowerCase() : '';
  }

  /* ────────────────────────────────────────
     Promo state
  ──────────────────────────────────────── */

  var promoState = {
    promo: null,             // merged campaign config (API + PROMO_CONFIG fallback)
    bxgyRaw: null,           // raw API entry for slot/handle helpers
    productMap: null,        // { slotLabel: numericId } for the picker (e.g. {serum, cream})
    firstFreeId: null,       // numeric ID used for auto-add (default slot)
    variantCacheBySlot: null, // { slotLabel: variantId } so addFreeQty doesn't
                              // re-fetch /products/<handle>.js on every click
                              // (Cloudflare 429s us if we hammer that endpoint)
    cartCache: null,         // last fetched cart
    syncInProgress: false,   // one-flight lock for cart mutations
    pendingRender: false,    // re-render request queued behind a sync
    inflightCartFetch: null, // dedupe overlapping /cart.js fetches
  };

  // Source label used on every pubsub publish we make. Subscribers (incl.
  // our own onExternalCartChange) check this to skip echoes of their own
  // changes — without it, publish→subscribe→fetch→publish becomes a loop.
  var SOURCE_LABEL = PROMO_CONFIG.sourceLabel;

  /* ────────────────────────────────────────
     1. Bootstrap: fetch promo config + cart
     ────────────────────────────────────────

     Decoupled from the promo card element so this script also drives
     auto-add on pages where no card is rendered (PDP, collection pages,
     checkout entry from a quick-add). The card UI render is gated on
     element presence; the cart-mutation logic always runs as long as
     a matching active promo exists.

     Without this, a customer who quick-adds 6 SCMS from a Marini PDP
     and clicks "Checkout" in the drawer skips /pages/carts entirely,
     never triggers auto-add, and reaches checkout with no Hyla3D in
     cart — losing the freebie they qualified for.
  */

  document.addEventListener('DOMContentLoaded', bootstrap);

  function bootstrap() {
    // PromoCompat (assets/promo-compat.js) decides which campaigns "win"
    // when two productDiscounts:false promos overlap on the same cart.
    // We await its decision before picking our BXGY promo so a higher-
    // savings incompatible sibling can suppress us cleanly. Fail-open if
    // the helper isn't present (older theme deploys, script error).
    var compatReady = (window.PromoCompat && window.PromoCompat.ready) || Promise.resolve(null);
    Promise.all([fetchActivePromos(), fetchCart(), compatReady])
      .then(function (results) {
        var promos = results[0];
        var cart = results[1];
        var compat = results[2];
        var customerBrand = getCustomerBrand();

        // Filter to applicable promos before picking ours.
        //
        // Two gates run here:
        //   1. PromoCompat — drops the loser when two
        //      productDiscounts:false promos overlap on this cart.
        //   2. Audience — STRICT for bxgy. The campaign's discount in
        //      Shopify is segment-gated to customers with a matching
        //      `source_brand` metafield. If the current customer
        //      doesn't match (incl. guests with empty brand), the
        //      Function won't fire at checkout — auto-injecting
        //      freebies would just bill them full price for items the
        //      UI labels "FREE". min-qty / bundle scripts use a laxer
        //      gate because they only render banners; bxgy mutates the
        //      cart, so it has to refuse cleanly.
        var availablePromos = (promos.promos || []).filter(function (p) {
          if (compat && compat.byId && compat.byId[p.campaignId] && !compat.byId[p.campaignId].applicable) {
            return false;
          }
          if (p.audience && p.audience.brand) {
            if (!customerBrand || p.audience.brand !== customerBrand) return false;
          }
          return true;
        });

        // Pick the first matching promo with at least one qualifying
        // + one free product. (For Q2 there's only one, but the API
        // returns an array — be permissive.)
        var bxgy = availablePromos.find(function (p) { return p.type === PROMO_CONFIG.campaignType; });
        if (!bxgy) {
          var noPromoCard = document.querySelector('[data-bxgy-promo]');
          if (noPromoCard) {
            noPromoCard.dataset.state = 'no-promo';
            noPromoCard.classList.add('hidden');
          }
          return;
        }

        // Merged campaign config. Each per-campaign field reads from the
        // API response when present and falls back to PROMO_CONFIG defaults
        // otherwise (see docs/bxgy-api-contract.md). Once BR Connect ships
        // the fields, the storefront picks them up with no theme deploy.
        promoState.promo = {
          // ── API-provided identity ──
          campaignId: bxgy.campaignId,
          title: bxgy.title,
          threshold: bxgy.threshold,
          qualifyingIds: (bxgy.qualifyingProductIds || []).map(gidToNumericId).filter(Boolean),
          freeIds: (bxgy.freeProductIds || []).map(gidToNumericId).filter(Boolean),
          requiresCode: bxgy.requiresCode,
          // ── Per-campaign config (API overrides PROMO_CONFIG) ──
          brandFreeName: bxgy.brandFreeName || PROMO_CONFIG.brandFreeName,
          freeFlagShortLabel: bxgy.freeFlagShortLabel || PROMO_CONFIG.freeFlagShortLabel,
          retailValuePerFreebieCents: typeof bxgy.retailValuePerFreebieCents === 'number'
            ? bxgy.retailValuePerFreebieCents
            : PROMO_CONFIG.retailValuePerFreebieCents,
          retailValueLabel: bxgy.retailValueLabel || PROMO_CONFIG.retailValueLabel,
          flyerUrl: bxgy.flyerUrl || PROMO_CONFIG.flyerUrl || null,
        };

        // Stash raw API response so handle-resolution helpers can read
        // slots[] without re-plumbing it through every call site.
        promoState.bxgyRaw = bxgy;

        // Slot resolution: prefer API-provided slots[] (each carries
        // productId GID + label + handle so no Shopify product fetch is
        // needed); fall back to PROMO_CONFIG's hardcoded numeric-ID map.
        var slotMap = buildHandleByNumericId(bxgy);
        var defaultFreeSlot = resolveDefaultFreeSlot(bxgy);

        return resolveProductMap(promoState.promo.freeIds, slotMap).then(function (map) {
          promoState.productMap = map;
          promoState.firstFreeId = map[defaultFreeSlot] || promoState.promo.freeIds[0];
          promoState.cartCache = cart;
          // Pre-fetch the free products' variant IDs once. addFreeQty reads
          // from this cache instead of hitting /products/<handle>.js per
          // click — that endpoint gets Cloudflare-rate-limited fast when a
          // practitioner taps the +/- mix buttons in quick succession.
          // Errors here are non-fatal; addFreeQty falls back to live fetch.
          preloadFreeVariants();

          // Wire the in-page "See how it works" modal trigger(s). Liquid
          // renders the trigger button with .hidden + an empty <iframe>;
          // we un-hide the button, attach click → showModal, and lazy-set
          // the iframe src on first open. No-op when flyerUrl is null.
          setFlyerControls(promoState.promo.flyerUrl);

          // Render the in-cart promo card if we're on a page that has
          // the multi-vendor cart layout. Elsewhere (PDP, collection,
          // etc.) we silently drive auto-add only.
          var card = document.querySelector('[data-bxgy-promo]');
          if (card) {
            card.classList.remove('hidden');
            render();
          }

          // Hydrate PDP banner if the current page is a product detail
          // page that ships our snippet. Gated by qualifyingIds inside
          // initPdp() — non-qualifying products keep the skeleton hidden.
          initPdp();

          // Listen for cart changes from anywhere. The theme uses two
          // notification mechanisms — we subscribe to both so PDP
          // forms, quick-add bulk, the brand cart drawer, and the
          // multi-vendor manager all funnel through this handler.
          //
          //   DOM event 'cart:updated'      — multi-vendor manager + us
          //   DOM event 'cart:item-added'   — addProductToCart in global.js
          //   pubsub  PUB_SUB_EVENTS.cartUpdate ('cart-update')
          //                                 — product-form.js (PDP),
          //                                   quick-add-bulk.js,
          //                                   brand-cart-drawer.js,
          //                                   cart.js (cart-items)
          document.addEventListener('cart:updated', onExternalCartChange);
          document.addEventListener('cart:item-added', onExternalCartChange);
          // See notifyCartChanged for why PUB_SUB_EVENTS is referenced
          // via lexical scope rather than `window.PUB_SUB_EVENTS`.
          var pseEvents = (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS : null;
          var cartUpdateEvent = pseEvents ? pseEvents.cartUpdate : 'cart-update';
          if (typeof window.subscribe === 'function') {
            window.subscribe(cartUpdateEvent, onExternalCartChange);
          }

          // Initial reconciliation: if cart already has 6+ SCMS but no
          // free Hyla3D, auto-add. If cart has stale free Hyla3D from a
          // prior session and SCMS is below threshold, removeFreeQty
          // takes care of it.
          syncFreeQty();

          // Signal readiness for cross-script consumers (cart drawer).
          // The drawer may have rendered freebie line items BEFORE this
          // bootstrap completed — in that path it falls back to locked
          // steppers because BxgyPromo.isReady() returns false. Firing
          // bxgy:ready lets the drawer re-render with unlocked steppers
          // once we're actually ready, even when syncFreeQty was a no-op
          // (delta=0) and didn't itself produce a cart:updated.
          window.dispatchEvent(new CustomEvent('bxgy:ready'));
        });
      })
      .catch(function (err) {
        console.warn('[bxgy-promo] init failed:', err);
        var card = document.querySelector('[data-bxgy-promo]');
        if (card) {
          card.dataset.state = 'error';
          card.classList.add('hidden');
        }
      });
  }

  /**
   * Re-fetch cart on any cart-changed signal and re-sync free items.
   * `cart:item-added` carries the variant id that was just added but
   * doesn't include the post-add cart state — fetching /cart.js fresh
   * keeps qualifying-qty math accurate even when multiple adds race.
   *
   * Two safety nets:
   *   1. Skip our own pubsub publishes (source === SOURCE_LABEL) so we
   *      don't loop publish → subscribe → fetch → publish.
   *   2. Debounce 200ms — DOM 'cart:updated' and pubsub 'cart-update'
   *      both fire for the same user action; without debouncing we'd
   *      hit /cart.js twice per add.
   */
  var onExternalCartChange = debounce(function (eventOrData) {
    if (!promoState.promo || !promoState.productMap) return;
    var src = null;
    if (eventOrData) {
      // Three shapes to recognize:
      //   pubsub:        { source, cartData }                   → .source
      //   our DOM event: { detail: { ...cart, _bxgyPromoSource } } → .detail._bxgyPromoSource
      //   legacy DOM:    { detail: { source, cartData } }       → .detail.source
      src = eventOrData.source
        || (eventOrData.detail && eventOrData.detail._bxgyPromoSource)
        || (eventOrData.detail && eventOrData.detail.source);
    }
    if (src === SOURCE_LABEL) return;

    fetchCart()
      .then(function (cart) {
        promoState.cartCache = cart;
        var card = document.querySelector('[data-bxgy-promo]');
        if (card) render();
        // PDP banner depends on cart's qualifying SCMS count, so it
        // also needs to re-render when the cart changes (e.g. user
        // navigated to PDP after adding SCMS elsewhere).
        renderPdp();
        syncFreeQty();
      })
      .catch(function (err) { console.warn('[bxgy-promo] cart refetch failed:', err); });
  }, 200);

  /**
   * Detect Shopify preview/theme-editor context. In preview we want to
   * also surface draft campaigns so the merchant can validate the
   * banner before launching. Real storefront traffic never sees drafts
   * (the banner would be misleading — no real Shopify discount yet).
   *
   * Detection vectors (any one is enough):
   *   - `Shopify.designMode === true` (theme editor iframe)
   *   - `?preview_theme_id=...` in the URL (preview link share)
   *   - hostname `*.shopifypreview.com` (preview link direct nav)
   *   - cookie `_shopify_y` paired with the preview-bar iframe in the
   *     DOM (preview link followed by an authenticated redirect onto
   *     the primary domain — Shopify keeps the preview-bar visible)
   */
  function isPreviewContext() {
    try {
      if (window.Shopify && window.Shopify.designMode === true) return true;
      var url = new URL(window.location.href);
      if (url.searchParams.has('preview_theme_id')) return true;
      if (/shopifypreview\.com$/.test(url.hostname)) return true;
      // Preview bar lives in an iframe at https://cdn.shopify.com/shopifycloud/preview-bar/...
      if (document.querySelector('iframe[src*="/preview-bar/"]')) return true;
    } catch (e) { /* fall through */ }
    return false;
  }

  function fetchActivePromos() {
    var qs = isPreviewContext() ? '?include_draft=1' : '';
    return fetchJson(BR_CONNECT_BASE + ACTIVE_PROMOS_PATH + qs);
  }

  // Single-flight + 429-tolerant. Multiple subscribers (DOM + pubsub) can
  // fire for the same user action; without dedupe we'd hit /cart.js 2-3×
  // per add and trip Shopify's storefront throttle on top of the brand
  // drawer's own fetches.
  function fetchCart() {
    if (promoState.inflightCartFetch) return promoState.inflightCartFetch;
    var p = fetchJsonResilient('/cart.js')
      .finally(function () { promoState.inflightCartFetch = null; });
    promoState.inflightCartFetch = p;
    return p;
  }

  /**
   * Refresh the header cart-icon-bubble after we mutate the cart.
   * The bubble lives in the {% section 'cart-icon-bubble' %} include and
   * doesn't auto-update on /cart/add.js — every component that mutates
   * the cart re-fetches that section via section_id rendering and swaps
   * the inner span. We mirror that pattern here so the header count
   * matches reality after auto-add / mix changes.
   *
   * Uses window.updateBubble (defined in assets/global.js) when available
   * — that helper also handles the brand-cart-drawer auto-open suppression
   * via skipOpenDrawer=true. Falls back to a manual swap if the helper
   * isn't loaded for some reason.
   */
  function refreshCartBubble() {
    return fetchResilient('/cart?section_id=cart-icon-bubble')
      .then(function (r) {
        if (!r || !r.ok) return '';
        return r.text();
      })
      .then(function (html) {
        if (!html) return;
        // Full innerHTML swap. window.updateBubble in this theme only
        // replaces #cart-icon-bubble's first <span> (the SVG wrapper) and
        // leaves the .cart-count-bubble div stale — so after our removal
        // the count would show the pre-removal total. Mirror what
        // brand-cart-drawer.updateCartBubble does: replace the entire
        // bubble container's contents with the freshly rendered section.
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = doc.querySelector('#shopify-section-cart-icon-bubble');
        var bubbleEl = document.getElementById('cart-icon-bubble');
        if (section && bubbleEl) {
          bubbleEl.innerHTML = section.innerHTML;
        }
      })
      .catch(function (err) {
        console.warn('[bxgy-promo] bubble refresh failed:', err);
      });
  }

  /**
   * Tell the rest of the theme that the cart changed. We use both
   * notification mechanisms because different components subscribe to
   * different ones — and not all of them listen for both:
   *
   *   DOM event 'cart:updated'    — multi-vendor-carts.js
   *   pubsub PUB_SUB_EVENTS.cartUpdate — brand-cart-drawer.js,
   *                                    product-form.js, cart.js,
   *                                    quick-add-bulk.js, moq-guard.js,
   *                                    price-per-item.js
   *
   * Without the pubsub publish, the brand cart drawer renders before
   * our auto-add resolves and shows only the user's SCMS — never
   * surfaces the freebie they just earned.
   *
   * Our own onExternalCartChange subscriber will also fire, but
   * syncFreeQty short-circuits when delta==0 so the loop is bounded
   * to one extra /cart.js fetch.
   */
  function notifyCartChanged(cart) {
    // DOM 'cart:updated' detail must be the raw cart object — the
    // multi-vendor-carts.js subscriber does `this.cartData = e.detail`
    // and then reads `cartData.items` to repopulate vendor cards. Wrapping
    // the cart in `{ source, cartData }` (as we used to) made the
    // subscriber assign the wrapper as cartData — items came back
    // undefined and ALL vendor cards on /pages/carts vanished after
    // every mix-editor click.
    //
    // We piggyback our source label on a private property so the same
    // event can be filtered by onExternalCartChange to break the
    // publish→subscribe→fetch→publish loop.
    var domDetail = Object.assign({}, cart, { _bxgyPromoSource: SOURCE_LABEL });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: domDetail }));
    // Pubsub keeps the wrapped { source, cartData } shape — that's what
    // brand-cart-drawer.js subscribes to and how it expects the source.
    // PUB_SUB_EVENTS is declared with `const` at the top of constants.js
    // so it's a global identifier but NOT a property of `window` — we
    // resolve it via the lexical scope chain (or fall back to the
    // string literal). publish() is `function`-declared so window.publish
    // works either way.
    var events = (typeof PUB_SUB_EVENTS !== 'undefined') ? PUB_SUB_EVENTS : null;
    var cartUpdateEvent = events ? events.cartUpdate : 'cart-update';
    if (typeof window.publish === 'function') {
      window.publish(cartUpdateEvent, {
        source: SOURCE_LABEL,
        cartData: cart,
      });
    }
    return refreshCartBubble();
  }

  /**
   * Build a numeric-ID → slot-label map for the campaign.
   *
   * Priority order:
   *   1. API-provided `slots[]` (Round 2B) — each entry has productId
   *      (GID), label (internal slot key), and handle. We extract the
   *      numeric portion of productId and key by it.
   *   2. PROMO_CONFIG.freeProductHandleByNumericId — Q2 hardcoded map.
   *
   * The label values must match the data-bxgy-count attributes baked
   * into the Liquid snippet. For Marini Q2 those are "serum" and
   * "cream"; future campaigns either reuse those slot keys or update
   * the Liquid (Round 2C scope).
   */
  function buildHandleByNumericId(bxgy) {
    if (Array.isArray(bxgy.slots) && bxgy.slots.length > 0) {
      var map = {};
      bxgy.slots.forEach(function (s) {
        var n = gidToNumericId(s.productId);
        if (n && s.label) map[n] = s.label;
      });
      return map;
    }
    return PROMO_CONFIG.freeProductHandleByNumericId;
  }

  /**
   * Slot label that gets auto-added when threshold is crossed.
   * API surfaces this as `defaultFreeSlotIndex` (position into slots[]),
   * which we resolve to the slot's `label`. Falls back to PROMO_CONFIG.
   */
  function resolveDefaultFreeSlot(bxgy) {
    if (Array.isArray(bxgy.slots) && typeof bxgy.defaultFreeSlotIndex === 'number') {
      var s = bxgy.slots[bxgy.defaultFreeSlotIndex];
      if (s && s.label) return s.label;
    }
    return PROMO_CONFIG.defaultFreeSlot;
  }

  /**
   * Resolve { slotLabel: numericId } from `freeProductIds` using the
   * provided numericId-to-slot map. Returns a Promise for symmetry with
   * legacy callers; body is synchronous.
   */
  function resolveProductMap(freeNumericIds, handleByNumericId) {
    var result = {};
    freeNumericIds.forEach(function (id) {
      var slot = handleByNumericId[id];
      if (slot) result[slot] = id;
    });
    return Promise.resolve(result);
  }

  /**
   * Wire the "See how it works" modal trigger(s) on the PDP banner.
   *
   * The Liquid snippet renders one or more `<button data-bxgy-flyer-trigger>`
   * elements (one per state) plus a single `<dialog data-bxgy-modal>`
   * containing an empty `<iframe data-bxgy-modal-frame>`.
   *
   * On first click we:
   *   1. Lazily set the iframe src (so we don't fetch the PDF until the
   *      practitioner actually wants to read it).
   *   2. Open the dialog with showModal() — gives us native focus
   *      management, Esc-to-close, and a backdrop pseudo-element we
   *      can dim via CSS.
   *
   * Backdrop click + close-button click both close the dialog. Esc is
   * native to <dialog>.
   *
   * When url is null (no flyer configured for this campaign), we leave
   * the buttons hidden — there's nothing to open.
   */
  function setFlyerControls(url) {
    var triggers = document.querySelectorAll('[data-bxgy-flyer-trigger]');
    var dialog = document.querySelector('[data-bxgy-modal]');

    if (!url || !dialog) {
      triggers.forEach(function (b) { b.classList.add('hidden'); });
      return;
    }

    triggers.forEach(function (btn) {
      btn.classList.remove('hidden');
      // Idempotent — re-running setFlyerControls (e.g. on cart-update
      // re-renders) shouldn't stack listeners. We tag the element after
      // first wiring and skip on subsequent runs.
      if (btn.dataset.bxgy_flyerWired === '1') return;
      btn.dataset.bxgy_flyerWired = '1';
      btn.addEventListener('click', function () {
        var iframe = dialog.querySelector('[data-bxgy-modal-frame]');
        // Append PDF Open-Parameters so Chrome/Edge hide their built-in
        // viewer chrome (toolbar, sidebar, scrollbar) and fit width on
        // load. Browsers that don't honor these params just ignore them.
        var srcWithViewerHints = url +
          (url.indexOf('#') === -1 ? '#' : '&') +
          'toolbar=0&navpanes=0&scrollbar=0&view=FitH';
        if (iframe && iframe.getAttribute('src') !== srcWithViewerHints) {
          iframe.setAttribute('src', srcWithViewerHints);
        }
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          // Old-browser fallback (no native <dialog>): just toggle the
          // [open] attribute. CSS still positions it as a modal.
          dialog.setAttribute('open', '');
        }
      });
    });

    if (dialog.dataset.bxgy_modalWired === '1') return;
    dialog.dataset.bxgy_modalWired = '1';

    var closeBtn = dialog.querySelector('[data-bxgy-modal-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () { dialog.close(); });
    }
    // Backdrop click closes — clicks on the dialog itself land on the
    // dim area outside the modal box; clicks inside the iframe / close
    // button never reach the dialog as ev.target.
    dialog.addEventListener('click', function (ev) {
      if (ev.target === dialog) dialog.close();
    });
  }

  // Resolve the handle for a slot (e.g. "serum" → "hyla3d-serum"). API
  // slots[] carries `handle` directly; legacy fallback reads from
  // PROMO_CONFIG. Used by addFreeQty for the /products/{h}.js lookup.
  function handleForSlot(slot) {
    var bxgy = promoState.bxgyRaw;
    if (bxgy && Array.isArray(bxgy.slots)) {
      var match = bxgy.slots.find(function (s) { return s.label === slot; });
      if (match && match.handle) return match.handle;
    }
    return PROMO_CONFIG.freeProductHandlesBySlot[slot] || null;
  }

  /* ────────────────────────────────────────
     2. Compute current state from cart
  ──────────────────────────────────────── */

  /** Number of qualifying SCMS units across the cart. */
  function getQualifyingQty(cart) {
    if (!cart || !cart.items) return 0;
    var ids = promoState.promo.qualifyingIds;
    return cart.items.reduce(function (sum, item) {
      if (ids.indexOf(String(item.product_id)) !== -1) return sum + item.quantity;
      return sum;
    }, 0);
  }

  /** Map of free Hyla3D currently in cart, split into "ours" (added by
   *  this script — has the FREE_FLAG property) and "theirs" (added by
   *  the customer manually, full price, no flag). */
  function getFreeLines(cart) {
    var serumId = promoState.productMap.serum;
    var creamId = promoState.productMap.cream;

    var out = {
      ours: { serum: 0, cream: 0, lines: [] },     // lines tagged with FREE_FLAG
      theirs: { serum: 0, cream: 0, lines: [] },   // customer-added Hyla3D
    };

    (cart.items || []).forEach(function (item) {
      var pid = String(item.product_id);
      var slot = pid === serumId ? 'serum' : pid === creamId ? 'cream' : null;
      if (!slot) return;

      var ours = item.properties && item.properties[FREE_FLAG] === '1';
      var bucket = ours ? out.ours : out.theirs;
      bucket[slot] += item.quantity;
      bucket.lines.push({ key: item.key, slot: slot, quantity: item.quantity });
    });

    return out;
  }

  /* ────────────────────────────────────────
     3. Render
  ──────────────────────────────────────── */

  function render() {
    var card = document.querySelector('[data-bxgy-promo]');
    if (!card || !promoState.promo || !promoState.cartCache) return;

    var loadingEl = card.querySelector('[data-bxgy-loading]');
    var belowEl = card.querySelector('[data-bxgy-below]');
    var qualifiedEl = card.querySelector('[data-bxgy-qualified]');

    var qualifyingQty = getQualifyingQty(promoState.cartCache);
    var threshold = promoState.promo.threshold;
    var freeLines = getFreeLines(promoState.cartCache);
    var freeOwned = freeLines.ours.serum + freeLines.ours.cream;

    loadingEl.classList.add('hidden');

    // Relevance gate: when the cart has ZERO qualifying products, the
    // "Add N more SCMS" copy is noise — the customer is shopping
    // something else. Hide the card entirely; the PDP banner will
    // educate when they actually visit a SCMS PDP. This is the fix
    // for the bug where a Skin Zyme cart showed the BXGY banner even
    // though SCMS aren't part of that user's flow.
    if (qualifyingQty === 0) {
      card.dataset.state = 'no-relevance';
      card.classList.add('hidden');
      return;
    }

    if (qualifyingQty < threshold) {
      card.dataset.state = 'below';
      card.classList.remove('hidden');
      belowEl.classList.remove('hidden');
      qualifiedEl.classList.add('hidden');

      var need = threshold - qualifyingQty;
      var needEl = card.querySelector('[data-bxgy-need]');
      if (needEl) needEl.textContent = String(need);
      return;
    }

    // Qualifies (>= threshold)
    card.dataset.state = freeOwned >= qualifyingQty ? 'claimed' : 'qualifies';
    card.classList.remove('hidden');
    belowEl.classList.add('hidden');
    qualifiedEl.classList.remove('hidden');

    var verbEl = card.querySelector('[data-bxgy-claimed-verb]');
    if (verbEl) verbEl.textContent = "You're getting";

    var freeCountEl = card.querySelector('[data-bxgy-free-count]');
    if (freeCountEl) freeCountEl.textContent = String(qualifyingQty);

    var serumCountEl = card.querySelector('[data-bxgy-count="serum"]');
    var creamCountEl = card.querySelector('[data-bxgy-count="cream"]');
    if (serumCountEl) serumCountEl.textContent = String(freeLines.ours.serum);
    if (creamCountEl) creamCountEl.textContent = String(freeLines.ours.cream);

    var hintEl = card.querySelector('[data-bxgy-hint]');
    if (hintEl) {
      if (freeOwned < qualifyingQty) {
        hintEl.textContent = 'Adding ' + (qualifyingQty - freeOwned) + ' free ' + promoState.promo.brandFreeName + ' to your cart…';
      } else if (freeLines.theirs.serum + freeLines.theirs.cream > 0) {
        hintEl.textContent =
          'Note: You also have ' + (freeLines.theirs.serum + freeLines.theirs.cream) +
          ' ' + promoState.promo.brandFreeName + ' added at full price. Only the ones tagged "' +
          promoState.promo.freeFlagShortLabel + '" apply to the promo.';
      } else {
        hintEl.textContent = '';
      }
    }
  }

  /* ────────────────────────────────────────
     3b. PDP banner — surface offer pre-cart
     ────────────────────────────────────────

     The cart-context render() above drives the badge inside the
     vendor cart card. The PDP needs a different treatment: there is
     no "current state of the cart-for-this-brand" yet, only a future
     state — what the cart will look like AFTER the user clicks Add
     to Cart at the chosen qty. So we compute:

         total = (qualifying SCMS already in cart across all variants)
               + (PDP qty input value, if this PDP IS a qualifying SCMS)

     and render below/qualifies based on `total >= threshold`. The
     PDP qty input is watched live so the banner updates as the user
     taps the +/- stepper.

     The Add to Cart button label is enriched on qualifies ("+ Free
     Hyla3D") so the value prop is visible right at the click moment.

     Gating: `data-product-id` is set on the snippet at render time;
     we compare it to `promoState.promo.qualifyingIds`. Products not
     in that list keep the skeleton hidden and never wire listeners.
  ──────────────────────────────────────── */

  function initPdp() {
    var card = document.querySelector('[data-bxgy-promo-pdp]');
    if (!card || !promoState.promo) return;

    var productId = String(card.dataset.productId || '');
    var qualifyingIds = (promoState.promo.qualifyingIds || []).map(String);
    if (!productId || qualifyingIds.indexOf(productId) === -1) {
      card.dataset.state = 'not-qualifying';
      return;
    }

    card.classList.remove('hidden');

    // Find the PDP qty input. The theme uses Shopify's <quantity-input>
    // custom element wrapping a regular `<input name="quantity">`. We
    // listen to both `input` (typing) and `change` (stepper buttons,
    // which dispatch change after stepUp/stepDown) for live updates.
    var qtyInput = document.querySelector('input[name="quantity"]');
    if (qtyInput) {
      qtyInput.addEventListener('input', renderPdp);
      qtyInput.addEventListener('change', renderPdp);
    }

    renderPdp();
  }

  function renderPdp() {
    var card = document.querySelector('[data-bxgy-promo-pdp]');
    if (!card || !promoState.promo) return;
    if (card.dataset.state === 'not-qualifying') return;

    var qtyInput = document.querySelector('input[name="quantity"]');
    var pdpQty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!isFinite(pdpQty) || pdpQty < 0) pdpQty = 0;

    var threshold = promoState.promo.threshold;
    // Cart's qualifying SCMS — sums across all qualifying variants,
    // matching what the campaign actually requires. We exclude the
    // current line's pre-existing qty so we don't double-count: the
    // user's PDP qty IS the new-qty after submit (Shopify replaces
    // the qty for the same variant + properties on /cart/add.js,
    // not increments it… actually it INCREMENTS for matching variant,
    // so we DO need to add cart + pdp qty for the projection). The
    // user-facing meaning is "after I add this to cart". 1:1 either
    // direction is conservative — better than under-promising.
    var cartQualifying = promoState.cartCache
      ? getQualifyingQty(promoState.cartCache)
      : 0;
    var projectedTotal = cartQualifying + pdpQty;

    var loadingEl = card.querySelector('[data-bxgy-loading]');
    var belowEl = card.querySelector('[data-bxgy-below]');
    var qualifiedEl = card.querySelector('[data-bxgy-pdp-qualified]');
    if (loadingEl) loadingEl.classList.add('hidden');

    if (projectedTotal < threshold) {
      card.dataset.state = 'below';
      if (belowEl) belowEl.classList.remove('hidden');
      if (qualifiedEl) qualifiedEl.classList.add('hidden');

      var need = threshold - projectedTotal;
      var needEl = card.querySelector('[data-bxgy-pdp-need]');
      if (needEl) needEl.textContent = String(need);
      enrichCta(false, 0);
      return;
    }

    // Qualifies (>= threshold)
    card.dataset.state = 'qualifies';
    if (belowEl) belowEl.classList.add('hidden');
    if (qualifiedEl) qualifiedEl.classList.remove('hidden');

    // Free count = full projected total under 1:1 scaling.
    var freeCount = projectedTotal;
    var freeCountEl = card.querySelector('[data-bxgy-pdp-free-count]');
    if (freeCountEl) freeCountEl.textContent = String(freeCount);

    // Compute savings using the campaign-configured retail value (per
    // Anthony's flyer framing). Reads from promoState.promo, which
    // already merged the API value with PROMO_CONFIG fallback at boot.
    var savingsEl = card.querySelector('[data-bxgy-pdp-savings]');
    if (savingsEl) {
      var perUnitCents = promoState.promo.retailValuePerFreebieCents;
      if (typeof perUnitCents === 'number') {
        savingsEl.textContent = formatMoney(perUnitCents * freeCount);
      }
    }

    enrichCta(true, freeCount);
  }

  // Append "+ Free Hyla3D" to the Add to Cart button label when the
  // PDP qty puts the projected cart at or above threshold. We stash
  // the original label on the dataset on first run so we can restore
  // it without ever stripping past it (idempotent across re-renders).
  // The `bxgy-cta-enriched` class lets CSS allow text wrap on
  // narrow viewports — the theme's whitespace-nowrap default would
  // otherwise overflow the suffixed label off the right edge.
  function enrichCta(qualifies, freeCount) {
    var btn = document.querySelector('button[name="add"]');
    if (!btn) return;
    var labelSpan = btn.querySelector(':scope > span');
    if (!labelSpan) return;

    if (typeof btn.dataset.bxgy_originalLabel !== 'string') {
      btn.dataset.bxgy_originalLabel = labelSpan.textContent;
    }
    var original = btn.dataset.bxgy_originalLabel;

    if (qualifies && freeCount > 0) {
      labelSpan.textContent = original.trim() + ' + ' + freeCount + ' Free ' + promoState.promo.brandFreeName;
      btn.classList.add('bxgy-cta-enriched');
    } else {
      labelSpan.textContent = original;
      btn.classList.remove('bxgy-cta-enriched');
    }
  }

  // Format cents → "A$140" (or shop currency). Mirrors what the cart
  // drawer does: Intl.NumberFormat with currency from cart, fallback
  // to USD. We don't import the drawer's helper to avoid coupling.
  function formatMoney(cents) {
    var amount = cents / 100;
    var currency = (promoState.cartCache && promoState.cartCache.currency) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(0);
    }
  }

  /* ────────────────────────────────────────
     4. Reconcile cart: ensure (free count) = (SCMS count)
  ──────────────────────────────────────── */

  /**
   * Ensure cart contains exactly `qualifyingQty` free Hyla3D lines (across
   * Serum + Cream combined). Below threshold → remove all free lines.
   * Above threshold → add or remove from the highest-count slot to balance.
   *
   * One-flight: if a sync is already running, queue a re-render and bail.
   * The next `cart:updated` will re-trigger this.
   */
  function syncFreeQty() {
    if (promoState.syncInProgress) {
      promoState.pendingRender = true;
      return;
    }

    var cart = promoState.cartCache;
    if (!cart) return;

    var qualifyingQty = getQualifyingQty(cart);
    var threshold = promoState.promo.threshold;
    var freeLines = getFreeLines(cart);
    var currentFree = freeLines.ours.serum + freeLines.ours.cream;

    var targetFree = qualifyingQty < threshold ? 0 : qualifyingQty;
    var delta = targetFree - currentFree;

    if (delta === 0) return;

    promoState.syncInProgress = true;
    setMixLoading(true);

    var promise;
    if (delta > 0) {
      // First qualify (no freebies in cart yet) with a multi-slot promo:
      // split 50/50 across the first two slots. Odd N puts the extra on
      // slot A (default), so 7 → 4/3, 9 → 5/4. Reads as a balanced
      // "Serum or Cream" default instead of all-serum / 0-cream.
      // Subsequent additions (user adds more SCMS later) skip the split
      // and go to firstFreeId so we don't disrupt a user-edited mix.
      var slotKeys = Object.keys(promoState.productMap || {});
      var canSplit = currentFree === 0 && delta >= 2 && slotKeys.length >= 2;
      if (canSplit) {
        var slotA = slotKeys[0];
        var slotB = slotKeys[1];
        var qtyA = Math.ceil(delta / 2);
        var qtyB = delta - qtyA;
        promise = addFreeQty(promoState.productMap[slotA], qtyA).then(function (resA) {
          mergeAddedLinesIntoCache(resA);
          if (qtyB <= 0) return null;
          return addFreeQty(promoState.productMap[slotB], qtyB).then(function (resB) {
            mergeAddedLinesIntoCache(resB);
            return null; // outer .then sees null and merges nothing further
          });
        });
      } else {
        promise = addFreeQty(promoState.firstFreeId, delta);
      }
    } else {
      // Remove |delta| from the slot with most free units. Bias removals
      // away from the user-chosen mix (start with serum if equal).
      var toRemove = Math.abs(delta);
      promise = removeFreeQty(toRemove, freeLines.ours);
    }

    promise
      .then(function (mutationResult) {
        // delta > 0: mutationResult is /cart/add.js response (line items)
        // delta < 0: mutationResult is /cart/change.js response (full cart)
        if (delta > 0) {
          mergeAddedLinesIntoCache(mutationResult);
        } else if (mutationResult && mutationResult.items) {
          promoState.cartCache = mutationResult;
        }
        render();
        return notifyCartChanged(promoState.cartCache);
      })
      .catch(function (err) {
        console.error('[bxgy-promo] syncFreeQty failed:', err);
      })
      .finally(function () {
        promoState.syncInProgress = false;
        setMixLoading(false);
        if (promoState.pendingRender) {
          promoState.pendingRender = false;
          syncFreeQty();
        }
      });
  }

  /* ────────────────────────────────────────
     5. Cart mutations
  ──────────────────────────────────────── */

  // Returns the response from /cart/add.js (the new line items),
  // which carry .key/.product_id/.quantity/etc. — enough to merge into
  // cartCache locally without a follow-up /cart.js fetch.
  /**
   * One-time fetch of all free products' first-available variant IDs at
   * bootstrap. Cached in promoState.variantCacheBySlot keyed by slot
   * label ({serum, cream}) so addFreeQty can skip the per-click fetch
   * entirely. Failures here are silent — addFreeQty falls back to live
   * /products/<handle>.js with retry.
   */
  function preloadFreeVariants() {
    if (!promoState.productMap) return Promise.resolve();
    var slots = Object.keys(promoState.productMap);
    var cache = {};
    var fetches = slots.map(function (slot) {
      var handle = handleForSlot(slot);
      if (!handle) return Promise.resolve();
      return fetchJsonResilient('/products/' + handle + '.js').then(function (product) {
        var firstAvailable = (product.variants || []).find(function (v) { return v.available; })
          || (product.variants || [])[0];
        if (firstAvailable) cache[slot] = firstAvailable.id;
      }).catch(function (err) {
        console.warn('[bxgy-promo] preload variant for', slot, 'failed:', err);
      });
    });
    return Promise.all(fetches).then(function () {
      promoState.variantCacheBySlot = cache;
    });
  }

  function addFreeQty(productNumericId, qty) {
    if (qty <= 0) return Promise.resolve(null);
    // Find which slot label this numeric ID belongs to, then look up
    // the slot's product handle. Both lookups go through helpers that
    // prefer API-provided slots[] when present.
    var slot = matchSlot({ product_id: productNumericId });
    if (!slot) {
      console.error('[bxgy-promo] addFreeQty: no slot for product', productNumericId, promoState.productMap);
      return Promise.reject(new Error('No slot resolved for product ' + productNumericId));
    }

    var properties = (function () { var p = {}; p[FREE_FLAG] = '1'; return p; })();
    var addLineForVariant = function (variantId) {
      return fetchResilient('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty, properties: properties }] }),
      }).then(function (res) {
        if (!res.ok) return res.text().then(function (t) {
          throw new Error('cart/add ' + res.status + ': ' + t);
        });
        return res.json();
      });
    };

    // Fast path — preloaded variant is the common case after bootstrap.
    var cachedId = promoState.variantCacheBySlot && promoState.variantCacheBySlot[slot];
    if (cachedId) return addLineForVariant(cachedId);

    // Slow path — preload missed (offline at bootstrap, or new slot added
    // mid-session). One-time live fetch + populate cache so subsequent
    // calls don't repeat the round-trip.
    var handle = handleForSlot(slot);
    if (!handle) {
      console.error('[bxgy-promo] addFreeQty: no handle for slot', slot, promoState.productMap);
      return Promise.reject(new Error('No handle resolved for slot ' + slot));
    }
    return fetchJsonResilient('/products/' + handle + '.js').then(function (product) {
      var firstAvailable = (product.variants || []).find(function (v) { return v.available; })
        || (product.variants || [])[0];
      if (!firstAvailable) throw new Error('No variant available for free product ' + handle);
      promoState.variantCacheBySlot = promoState.variantCacheBySlot || {};
      promoState.variantCacheBySlot[slot] = firstAvailable.id;
      return addLineForVariant(firstAvailable.id);
    });
  }

  // Match two cart lines as the "same logical line" — same variant id
  // and same line-item properties. We can't rely on `key` because
  // Shopify regenerates the key after some mutations even when the
  // logical line (variant + properties) is unchanged, which used to
  // make us push duplicate rows.
  function sameLogicalLine(a, b) {
    if (!a || !b) return false;
    if (String(a.variant_id) !== String(b.variant_id)) return false;
    var ap = a.properties || {};
    var bp = b.properties || {};
    var ak = Object.keys(ap).sort();
    var bk = Object.keys(bp).sort();
    if (ak.length !== bk.length) return false;
    for (var i = 0; i < ak.length; i++) {
      if (ak[i] !== bk[i]) return false;
      if (String(ap[ak[i]]) !== String(bp[bk[i]])) return false;
    }
    return true;
  }

  // Merge new lines from /cart/add.js response into cartCache. Shopify
  // returns the line at its post-merge total quantity, so we replace
  // the existing logical line in place (matching by variant + props,
  // NOT by key — see sameLogicalLine above for rationale).
  function mergeAddedLinesIntoCache(addResponse) {
    if (!promoState.cartCache || !addResponse) return;
    var newLines = addResponse.items || [addResponse];
    var cart = promoState.cartCache;
    cart.items = cart.items || [];
    newLines.forEach(function (newItem) {
      if (!newItem) return;
      var idx = cart.items.findIndex(function (i) { return sameLogicalLine(i, newItem); });
      if (idx >= 0) {
        var prevQty = cart.items[idx].quantity || 0;
        cart.items[idx] = newItem;
        cart.item_count = (cart.item_count || 0) + (newItem.quantity - prevQty);
      } else {
        cart.items.push(newItem);
        cart.item_count = (cart.item_count || 0) + (newItem.quantity || 0);
      }
    });
  }

  /**
   * Remove `qty` units from the user's free lines. Preferred order:
   * Cream first if Cream has the most, else Serum first. /cart/change.js
   * returns the full cart, so the last response is the authoritative
   * post-mutation cart — no follow-up /cart.js needed.
   */
  function removeFreeQty(qty, owned) {
    if (qty <= 0) return Promise.resolve(null);
    var preferSlot = owned.serum >= owned.cream ? 'serum' : 'cream';

    var ourLines = ((promoState.cartCache && promoState.cartCache.items) || []).filter(function (i) {
      return i.properties && i.properties[FREE_FLAG] === '1';
    });
    ourLines.sort(function (a, b) {
      var aMatch = matchSlot(a) === preferSlot ? 0 : 1;
      var bMatch = matchSlot(b) === preferSlot ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return b.quantity - a.quantity;
    });

    var seq = Promise.resolve(null);
    var remaining = qty;
    ourLines.forEach(function (line) {
      if (remaining <= 0) return;
      var removeFromThis = Math.min(remaining, line.quantity);
      var newQty = line.quantity - removeFromThis;
      seq = seq.then(function () {
        return fetchResilient('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: newQty }),
        }).then(function (res) {
          if (!res.ok) return res.text().then(function (t) {
            throw new Error('cart/change ' + res.status + ': ' + t);
          });
          return res.json();
        });
      });
      remaining -= removeFromThis;
    });
    return seq;
  }

  // Find which slot label a cart line belongs to by reverse-lookup on
  // promoState.productMap. Generic over any number of slots so adding
  // a third free-product flavor is a config change, not a code change.
  function matchSlot(item) {
    if (!promoState.productMap) return null;
    var pid = String(item.product_id);
    var labels = Object.keys(promoState.productMap);
    for (var i = 0; i < labels.length; i++) {
      if (pid === String(promoState.productMap[labels[i]])) return labels[i];
    }
    return null;
  }

  /* ────────────────────────────────────────
     6. Rebalance Serum vs Cream
  ──────────────────────────────────────── */

  /**
   * Toggle the in-flight visual cue while a rebalance is mid-air.
   * The cart-card's headline ✓ icon swaps to a spinner via the
   * `.bxgy-promo.is-syncing` class. (The interactive picker lives in
   * the cart drawer, which manages its own per-line stepper disable
   * state via brand-cart-drawer.js.)
   */
  function setMixLoading(loading) {
    var card = document.querySelector('[data-bxgy-promo]');
    if (card) card.classList.toggle('is-syncing', loading);
  }

  /**
   * Rebalance: changing Serum +1 means Cream −1 (and vice versa). The
   * total free count stays equal to qualifyingQty.
   *
   * UX: optimistic UI — count visibly moves on click, before the
   * network request fires. render() at the end overwrites with
   * authoritative cart state (or, on error, re-renders the unchanged
   * cartCache to roll back). Combined with the .is-loading dim/spinner
   * the click feels instant even though /cart/change.js + /cart/add.js
   * take ~1-2s round-trip.
   */
  // No debounce: the syncInProgress lock below catches rapid double-
  // clicks safely, and a leading 300ms debounce delayed the optimistic
  // count update — it made the click feel laggy rather than smoothing
  // anything. Run immediately, lock immediately.
  function rebalanceMix(slot, sign) {
    if (promoState.syncInProgress) {
      console.log('[bxgy-promo] rebalance skipped: sync in progress');
      return;
    }
    var freeLines = getFreeLines(promoState.cartCache);
    var owned = freeLines.ours;
    console.log('[bxgy-promo] rebalance request:', { slot: slot, sign: sign, owned: owned });

    if (sign === 1) {
      var otherSlot = slot === 'serum' ? 'cream' : 'serum';
      if (owned[otherSlot] < 1) {
        console.log('[bxgy-promo] rebalance no-op: nothing to take from', otherSlot);
        return;
      }
    } else {
      if (owned[slot] < 1) {
        console.log('[bxgy-promo] rebalance no-op: nothing to remove from', slot);
        return;
      }
    }

    var loser = sign === 1 ? (slot === 'serum' ? 'cream' : 'serum') : slot;
    var winner = sign === 1 ? slot : (slot === 'serum' ? 'cream' : 'serum');
    var loserFakeOwned = { serum: 0, cream: 0 };
    loserFakeOwned[loser] = owned[loser];
    console.log('[bxgy-promo] rebalance: -1', loser, '+1', winner);

    // Optimistic UI: instantly reflect the new mix in the count cells
    // so the click feels responsive. render() runs at the end with the
    // authoritative cart and either matches (success) or reverts (error).
    var card = document.querySelector('[data-bxgy-promo]');
    var loserCountEl = card && card.querySelector('[data-bxgy-count="' + loser + '"]');
    var winnerCountEl = card && card.querySelector('[data-bxgy-count="' + winner + '"]');
    if (loserCountEl) loserCountEl.textContent = String(owned[loser] - 1);
    if (winnerCountEl) winnerCountEl.textContent = String(owned[winner] + 1);

    promoState.syncInProgress = true;
    setMixLoading(true);

    removeFreeQty(1, loserFakeOwned)
      .then(function (cartAfterRemove) {
        // /cart/change.js returns the full cart — keep cache fresh so
        // the subsequent addFreeQty has accurate state for any next op.
        if (cartAfterRemove && cartAfterRemove.items) {
          promoState.cartCache = cartAfterRemove;
        }
        return addFreeQty(promoState.productMap[winner], 1);
      })
      .then(function (addResponse) {
        mergeAddedLinesIntoCache(addResponse);
        render();
        return notifyCartChanged(promoState.cartCache);
      })
      .catch(function (err) {
        console.error('[bxgy-promo] rebalance failed:', err);
        // Roll back the optimistic update — render() reads from cartCache
        // which is unchanged when the mutation rejects before completing.
        render();
      })
      .finally(function () {
        promoState.syncInProgress = false;
        setMixLoading(false);
      });
  }

  /* ────────────────────────────────────────
     7. Public API for cross-script callers
     ────────────────────────────────────────

     Exposed for the cart drawer (brand-cart-drawer.js) so it can let the
     customer rebalance Serum/Cream right from the freebie line items
     without reimplementing the rebalance logic. Single source of truth:
     all mix mutations go through rebalanceMix().

     Contract:
       isReady()                 → true once promoState.productMap is built
       getSlotForProduct(numId)  → "serum" | "cream" | null
       getOwned()                → { serum: N, cream: M }  (totals in cart)
       getThreshold()            → number (qualifying qty for 1 freebie)
       rebalance(slot, sign)     → kicks off rebalanceMix; returns Promise
                                   that settles after the mutation roundtrip
                                   (resolves with the freshly-fetched cart,
                                   rejects on network failure). Caller can
                                   show its own optimistic UI; render() in
                                   bxgy-promo runs at the end and updates
                                   any [data-bxgy-promo] surfaces too.

     If the API isn't ready (promo still loading), getSlotForProduct
     returns null — callers should treat the line as a regular item.
  */
  window.BxgyPromo = {
    isReady: function () {
      return !!(promoState.promo && promoState.productMap);
    },
    getSlotForProduct: function (productNumericId) {
      if (!promoState.productMap || productNumericId == null) return null;
      var pid = String(productNumericId);
      var labels = Object.keys(promoState.productMap);
      for (var i = 0; i < labels.length; i++) {
        if (pid === String(promoState.productMap[labels[i]])) return labels[i];
      }
      return null;
    },
    getOwned: function () {
      if (!promoState.cartCache) return { serum: 0, cream: 0 };
      var fl = getFreeLines(promoState.cartCache);
      return { serum: fl.ours.serum, cream: fl.ours.cream };
    },
    getThreshold: function () {
      return (promoState.promo && promoState.promo.threshold) || 0;
    },
    /**
     * Trigger a mix rebalance. Returns a Promise so the caller can
     * coordinate its own loading state. The internal rebalanceMix()
     * runs sync optimistic UI + setMixLoading + render — those are
     * additive and don't conflict with the caller's own UI.
     */
    rebalance: function (slot, sign) {
      return new Promise(function (resolve, reject) {
        if (promoState.syncInProgress) {
          reject(new Error('rebalance: sync in progress'));
          return;
        }
        if (!promoState.promo || !promoState.productMap) {
          reject(new Error('rebalance: promo not ready'));
          return;
        }
        // Hand off to rebalanceMix and use the syncInProgress flag flip
        // (back to false) to detect completion. Combined with checking
        // the cart owned counts for a delta we can resolve correctly.
        var beforeOwned = window.BxgyPromo.getOwned();
        rebalanceMix(slot, sign);
        // Poll until syncInProgress clears (set in the .finally of
        // rebalanceMix). Bounded by a 10s timeout to avoid hanging.
        var deadline = Date.now() + 10000;
        var iv = setInterval(function () {
          if (!promoState.syncInProgress) {
            clearInterval(iv);
            var afterOwned = window.BxgyPromo.getOwned();
            var changed =
              afterOwned.serum !== beforeOwned.serum ||
              afterOwned.cream !== beforeOwned.cream;
            if (changed) resolve(promoState.cartCache);
            else reject(new Error('rebalance: no change (failed or no-op)'));
          } else if (Date.now() > deadline) {
            clearInterval(iv);
            reject(new Error('rebalance: timeout'));
          }
        }, 50);
      });
    },
  };
})();
